import PropTypes from 'prop-types';
import { createRef, useEffect, useMemo, useState } from 'react';
import { Trans, withTranslation } from 'react-i18next';

import { getEnv } from '@ctct/ctct-helpers';
import { trackAction } from '@ctct/distui-helpers';
import { Alert, Button, Dialog, Icon, PageError, Spinner, StylizedBulletList, ThemeImage } from '@ctct/ui-react';

import smsStat from './assets/SMS_stat.webp';
import vector from './assets/Vector.webp';
import OrderSummary from './OrderSummary';
import SMSTiersCard from './SMSTiersCard';
import SMSTiersTable from './SMSTiersTable';
import { getBillingCustomerCurrency, getCountryCode, getIsTrialer } from '../../api/DistUI';
import MyAccountApiLite from '../../api/MyAccountApiLite';
import ProductSvc from '../../api/ProductSvc';
import { getStripeCustomer } from '../../api/StripeApi';
import AddOnIds from '../../constants/addOnIds';
import { getSupportPhoneNumber } from '../../utils/getSupportPhoneNumber';
import { observabilityAppService } from '../../utils/ObservabilityAppService';
import { transformCustomerInfo } from '../../utils/orderSummaryUtils';

const env = getEnv();

const accountBillingTabURL = `https://app${env}constantcontact.com/pages/myaccount/settings/billing`;

const {
  getBillingInfo,
  getPlanGroup,
  getPlanRating,
  payForPlan,
  getSMSTiers,
  getUserPlanOverview,
  getRMCValue,
  getBillingAccount,
} = MyAccountApiLite;

const cache = {};

async function memoGetRating(props) {
  const stripeKey = props.stripeAccount ? 'stripe' : 'no-stripe';
  const propsString = `${props.smsUsed}-${props.discountCode}-${props.selectedCurrency}-${props.discount?.discountPeriod}-${stripeKey}`;

  // Don't use cache if discountPeriod is invalid (0 or undefined)
  if (cache[propsString] && props.discount?.discountPeriod) {
    return { ...cache[propsString] };
  }

  const data = await getPlanRating({ ...props });

  if (data.isAxiosError) throw data;

  // Only cache if discountPeriod is valid
  if (props.discount?.discountPeriod) {
    cache[propsString] = { ...data };
  }

  return { ...data };
}

const SMSTierDialogWithOrderSummaryV2 = ({ t, open, closeDialog, smsPurchasedCallback, closeOnSuccess }) => {
  const [smsSelection, setSmsSelection] = useState({ min: 501, max: 1000, price: 20 });
  const [SMSTiers, setSMSTiers] = useState([]);
  const [SMSTiersComplete, setSMSTiersComplete] = useState(false);
  const [loadingSMSTiers, setLoadingSMSTiers] = useState(true);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState();
  const [overview, setOverview] = useState();
  const [paymentPlanGroupId, setPaymentPlanGroupId] = useState();
  const [planId, setPlanId] = useState();
  const [discountCode, setDiscountCode] = useState();
  const [appliedDiscountCode, setAppliedDiscountCode] = useState();
  const [discountError, setDiscountError] = useState();
  const [contactCount, setContactCount] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [addSmsAddon, setAddSmsAddon] = useState(false);
  const [successfulPayment, setSuccessfulPayment] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [rating, setRating] = useState();
  const [isTrialAccount, setIsTrialAccount] = useState(null);
  const [promoEnabled, setPromoEnabled] = useState(false);
  const [showSucessEmail, setShowSucessEmail] = useState(false);
  const [showAllTiers, setShowAllTiers] = useState(0);
  const [includesFeaturesList, setIncludesFeaturesList] = useState([]);
  const [prepayDiscountPercentage, setPrepayDiscountPercentage] = useState(null);
  const [nextBillDate, setNextBillDate] = useState();
  const [contactTierLabel, setContactTierLabel] = useState('');
  const [nextBillDateObject, setNextBillDateObject] = useState();
  const alertRef = createRef();
  const comparePlansRef = createRef();
  const [isStripeAccount, setIsStripeAccount] = useState(false);
  const [stripePaymentToken, setStripePaymentToken] = useState(null);
  const [accountCountryCode, setAccountCountryCode] = useState(null);

  const supportPhone = getSupportPhoneNumber(customer?.country);

  const showCardDecimals = useMemo(() => {
    if (SMSTiers && SMSTiers.length > 0) {
      for (let i = 0; i < 3; i += 1) {
        let retVal = new Intl.NumberFormat({
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format((SMSTiers[i].price * 100 * prepayDiscountPercentage) % 100);

        if (retVal.length === 1) {
          retVal = `0${retVal}`;
        }

        if (retVal !== '00') {
          return true;
        }
      }
    }

    return false;
  }, [SMSTiers, prepayDiscountPercentage]);

  useEffect(() => {
    if (errorMessage && alertRef) {
      alertRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [errorMessage]);

  useEffect(() => {
    const includesFeaturesListLocal = [];

    for (let i = 1; ; i += 1) {
      const stringKey = `sms-plans.dialog.sms-includes-list.${i}`;

      if (stringKey === t(stringKey)) {
        break;
      }

      includesFeaturesListLocal.push(t(stringKey));
    }
    setIncludesFeaturesList(
      includesFeaturesListLocal.map((feature) => {
        return {
          text: feature,
        };
      })
    );
  }, []);

  useEffect(() => {
    if (open) {
      setSuccessfulPayment(false);
      setShowAllTiers(0);
    }
  }, [open]);

  useEffect(() => {
    (async () => {
      if (open) {
        const isTrialer = await getIsTrialer();

        if (isTrialer) {
          setIsTrialAccount(true);
          window.location.href = `https://app${env}constantcontact.com/pages/myaccount/base/purchase/plans-and-pricing`;
        } else if (SMSTiers.length === 0) {
          try {
            trackAction({ action: 'stp-sms-sms-dialog-view' });
            setIsTrialAccount(false);

            const [localCurrency, localCountryCode] = await Promise.all([
              getBillingCustomerCurrency(),
              getCountryCode(),
            ]);

            setCurrency(localCurrency);
            setAccountCountryCode(localCountryCode);

            const [planGroupResult, result, { current_contacts: currentContactsStr }, billingAccount] =
              await Promise.all([
                getPlanGroup(localCurrency),
                getBillingInfo(),
                getUserPlanOverview(),
                getBillingAccount(),
              ]);

            const { paymentPlanId, currentPlanId } = planGroupResult;

            const isStripe = billingAccount?.billingSystemVersion >= 4;

            setIsStripeAccount(isStripe);

            if (isStripe) {
              try {
                const stripeCustomer = await getStripeCustomer();
                const paymentToken = stripeCustomer?.paymentMethod?.id;

                if (paymentToken) {
                  setStripePaymentToken(paymentToken);
                }
              } catch (e) {
                observabilityAppService.notify(e, { message: 'Could not  get stripe customer in SMS dialog' });
              }
            }

            const { customer: customerLocal, overview: overviewLocal } = transformCustomerInfo(result, localCurrency);

            setCustomer(customerLocal);
            setOverview(overviewLocal);
            setContactTierLabel(result?.overview?.contact_tier);
            setPaymentPlanGroupId(paymentPlanId);
            setPlanId(currentPlanId);
            setPromoEnabled(await getRMCValue('SMS_INTRODUCTORY_PROMOTION_ENABLED'));
            setShowSucessEmail(await getRMCValue('SMS_SHOW_SUCCESS_EMAIL'));

            const contactCountLocal = currentContactsStr ? parseInt(currentContactsStr.replace(/,/g, ''), 10) : 0;

            setContactCount(contactCountLocal);
            setLoadingSMSTiers(true);

            const priceTiers = await getSMSTiers(localCurrency, currentPlanId);

            setSMSTiers(priceTiers);

            const {
              overview: { next_charge_scheduled: nextChargeDate },
            } = result;
            const nextBillingDate = new Date(nextChargeDate);

            nextBillingDate.setDate(nextBillingDate.getDate() - 1);

            const dateOptions = { month: 'long', day: 'numeric' };

            if (nextBillingDate.getFullYear() !== new Date().getFullYear()) {
              dateOptions.year = 'numeric';
            }

            const displayDate = nextBillingDate.toLocaleString('default', dateOptions);

            setNextBillDate(displayDate);
            setNextBillDateObject(nextBillingDate);

            setTimeout(() => {
              setSMSTiersComplete(true);
            }, 50);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('exception getting billing information', e);
          } finally {
            setLoadingSMSTiers(false);
          }
        }
      }
    })();
  }, [open]);

  useEffect(() => {
    const transformErrors = (errors) => {
      const isPaymentError = (errorKey) => {
        switch (errorKey) {
          case 'error.pfp.make.payment':
          case 'BillingSVC exception - could not find requested information to retrieve or update':
          case 'payment.request.method.required':
          case 'error.pfp.payment.method.verification':
            return true;
          default:
            return false;
        }
      };

      let hasPaymentError = false;
      const errorString = errors
        .map((error) => {
          if (isPaymentError(error.error_key)) {
            hasPaymentError = true;
          }

          let retVal = t([`sms-plans.dialog.errors.${error.error_key}`, error.error_message]);

          if (!retVal.endsWith('.')) {
            retVal += '.';
          }

          return retVal;
        })
        .join('');

      setErrorMessage(
        hasPaymentError ? (
          <Trans
            i18nKey="sms-plans.dialog.errors.sms-payment-error-with-returned-error"
            t={t}
            values={{ errorString: `${errorString} `, supportPhone }}
          >
            <a href={accountBillingTabURL} target="_blank" rel="noopener noreferrer">
              billing page
            </a>
          </Trans>
        ) : (
          errorString
        )
      );
    };

    (async () => {
      if (addSmsAddon && rating) {
        try {
          setErrorMessage(null);

          const { stripeQuoteId } = rating || {};
          const isStripePayment = stripeQuoteId && isStripeAccount && stripePaymentToken;

          const payload = {
            customer,
            ratingData: rating,
            payment: isStripePayment
              ? {
                  paymentType: {
                    displayString: 'Stripe',
                    name: 'STRIPE',
                  },
                  stripe: {
                    token: stripePaymentToken,
                  },
                }
              : {
                  paymentType: {
                    displayString: 'onfile',
                    name: 'ONFILE',
                  },
                },
            ...(stripeQuoteId ? { stripeQuoteId } : null),
          };
          const response = await payForPlan(payload);

          if (response.ok) {
            if (smsPurchasedCallback) smsPurchasedCallback(smsSelection);

            if (closeOnSuccess) {
              closeDialog();
            } else {
              setSuccessfulPayment(true);
            }
          } else {
            const { errors } = await response.json();

            if (errors && errors.length > 0) {
              transformErrors(errors);
            }
          }
        } catch (err) {
          const {
            response: { data: errMessage },
          } = err;

          if (errMessage && typeof errMessage === 'string') {
            setErrorMessage(errMessage);
          } else {
            setErrorMessage(t('sms-plans.unknown_error'));
          }
        } finally {
          setAddSmsAddon(false);
        }
      }
    })();
  }, [addSmsAddon, rating]);

  useEffect(() => {
    if (rating) {
      const { addOnRating } = rating;
      const [firstAddon] = addOnRating;
      const { fullPrice, monthlyCost, period } = firstAddon || {};

      if (monthlyCost && fullPrice && monthlyCost !== fullPrice) {
        let calculatedPercentage;

        if (isStripeAccount) {
          // Stripe: fullPrice is total prepay (e.g., $480 for 12 months), divide by period
          const monthlyTierPrice = fullPrice / (period || 1);

          calculatedPercentage = monthlyCost / monthlyTierPrice;
        } else {
          // BaaS: fullPrice is already monthly
          calculatedPercentage = monthlyCost / fullPrice;
        }

        setPrepayDiscountPercentage(calculatedPercentage);
      } else {
        setPrepayDiscountPercentage(0);
      }
    }
  }, [rating, isStripeAccount]);

  useEffect(() => {
    // Don't call rating API if overview is not loaded yet or paymentPeriod is missing
    if (
      !customer ||
      !paymentPlanGroupId ||
      !planId ||
      !smsSelection ||
      contactCount == null ||
      !overview?.paymentPeriod
    )
      return;

    const getRating = async () => {
      try {
        setLoading(true);
        setRating(null);

        const updatedRating = await memoGetRating({
          newAddOnServices: [AddOnIds.CTCT_SMS_ADDON],
          customer,
          selectedCurrency: currency,
          discount: {
            discountPeriod: overview?.paymentPeriod,
          },
          contactUsed: contactCount || 0,
          smsUsed: smsSelection.min,
          paymentPlanGroupId,
          currentPlanId: planId,
          discountCode: appliedDiscountCode,
          ...(isStripeAccount ? { stripeAccount: true } : {}),
        });

        setRating(updatedRating);

        if (appliedDiscountCode) {
          const promotionDetails = updatedRating.promotionData && updatedRating.promotionData[appliedDiscountCode];

          if (!promotionDetails) {
            setDiscountError(t('order_summary.discount-code-error-default'));
            setAppliedDiscountCode(undefined);
            setDiscountAmount(0);

            return;
          }

          if (!promotionDetails.applied) {
            setDiscountError(promotionDetails.reasonMessage);
            setAppliedDiscountCode(undefined);
            setDiscountAmount(0);

            return;
          }

          setDiscountError(undefined);

          const { promotionCredit: appliedDiscountAmount, proratedPromotionCredit: proratedAppliedDiscountAmount } =
            promotionDetails;

          setDiscountAmount(proratedAppliedDiscountAmount || appliedDiscountAmount || 0);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('exception getting rating', e);

        if (appliedDiscountCode) {
          setAppliedDiscountCode(undefined);
          setDiscountAmount(0);
          setDiscountError(e.message);
        } else {
          setDiscountError(undefined);
        }
      } finally {
        setLoading(false);
      }
    };

    getRating();
  }, [customer, contactCount, currency, smsSelection, paymentPlanGroupId, planId, appliedDiscountCode, overview]);

  useEffect(() => {
    if (showAllTiers && SMSTiers && SMSTiers.length && SMSTiersComplete && comparePlansRef && comparePlansRef.current) {
      setTimeout(() => {
        comparePlansRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 1);
    }
  }, [showAllTiers, SMSTiers, SMSTiersComplete]);

  const smsFooterButtonsStyles = useMemo(() => {
    if (successfulPayment) return { justifyContent: 'center' };

    return { justifyContent: 'flex-end' };
  }, [successfulPayment]);

  const [smsSent, setSmsSent] = useState(0);
  const [hasPurchasedSms, setHasPurchasedSms] = useState(false);

  useEffect(() => {
    (async () => {
      const smsSendEntitlement = await ProductSvc.getSmsSendEntitlement();

      setSmsSent(smsSendEntitlement.allowance.used);
      // https://ctct.slack.com/archives/C033E8DRJKB/p1747140483649129
      setHasPurchasedSms(smsSendEntitlement.allowed || smsSendEntitlement.reasons?.includes('DISABLED'));
    })();
  }, []);

  if (isTrialAccount == null || isTrialAccount === true) return null;

  const isPremium = SMSTiers?.[0]?.price === 0;
  const isCurrent = (smsTier) => {
    if (!smsTier || !hasPurchasedSms) return false;

    return smsSent >= smsTier.tierMin && smsTier.tierMax >= smsSent;
  };

  return (
    <Dialog
      heading={t('sms-plans.dialog.title')}
      data-qe-id="sms-plans-dialog"
      className="sms-plans-dialogV2"
      contentFlush={false}
      kind="jumbo"
      isOpen={open}
      onDismiss={closeDialog}
      footerContent={
        !successfulPayment && (
          <div className="terms-and-conditions">
            <Trans
              i18nKey={`sms-plans.dialog.authorize-recurring-charge-${
                overview?.paymentPeriod > 1 ? 'prepay' : 'monthly'
              }`}
              t={t}
              values={{ supportPhone }}
            >
              <strong />
              <a
                href="https://www.constantcontact.com/legal/text-messaging-terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Constant Contact terms and condition
              </a>{' '}
            </Trans>
          </div>
        )
      }
      footerActions={
        <div className="sms-footer-buttons" style={smsFooterButtonsStyles}>
          <div className="action-buttons">
            {successfulPayment ? (
              <div>
                <Button
                  block
                  className="sidebar-close-button"
                  kind="primary"
                  data-qe-id="sms-plans-dialog-register-button"
                  onClick={() => {
                    window.location.href = `https://app${env}constantcontact.com/pages/myaccount/settings/sms`;
                  }}
                  text={t('sms-plans.register')}
                />
              </div>
            ) : (
              <Button
                block
                className="sidebar-continue-button"
                kind="primary"
                busy={addSmsAddon}
                data-qe-id="sms-plans-dialog-continue-button"
                onClick={() => {
                  trackAction({ action: 'stp-sms-button-select' });
                  setAddSmsAddon(true);
                }}
                text={t('sms-plans.add-sms')}
              />
            )}
          </div>
        </div>
      }
    >
      {errorMessage && (
        <div ref={alertRef}>
          <Alert kind="error" message={errorMessage} />
        </div>
      )}
      {successfulPayment ? (
        <PageError
          data-qe-id="feature-upgrade-success"
          image={<ThemeImage color="green" image="checkmark" size="large" data-qe-id="feature-upgrade-success-image" />}
          heading={<Trans i18nKey="sms-plans.dialog.success.heading" />}
          description={
            showSucessEmail && (
              <Trans i18nKey="sms-plans.dialog.success.body">
                An email was sent to {{ email: customer?.email }} confirming your plan selection. Next,{' '}
                <a href={`https://app${env}constantcontact.com/pages/myaccount/settings/sms`}>register for SMS</a> to
                get your sending number.
              </Trans>
            )
          }
        />
      ) : (
        <div className="sms-dialog-wrapper">
          <div className="first-two-columns">
            <div
              className="sticky-wrapper sms-header-panel"
              style={{ padding: 20, paddingLeft: 40, paddingTop: 70, flexBasis: 500 }}
            >
              <div style={{ fontWeight: 700, fontSize: 30, lineHeight: '40px', paddingBottom: 15 }}>
                {t('sms-plans.dialog.how-many-sms-messages')}
              </div>
              <img src={vector} alt="" style={{ height: 10, marginBottom: 15 }} />
              <div style={{ FontWeight: 400, FontSize: 16, lineHeight: '26px' }}>
                <div style={{ paddingBottom: 30 }}>
                  <Trans
                    i18nKey={
                      accountCountryCode?.toUpperCase() === 'US'
                        ? 'sms-plans.dialog.choose-the-starting-price'
                        : 'sms-plans.dialog.choose-the-starting-price-non-us'
                    }
                  >
                    <a
                      href="https://knowledgebase.constantcontact.com/articles/KnowledgeBase/45188-About-Constant-Contacts-SMS-tool?lang=en_US"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      some content
                    </a>
                  </Trans>
                </div>
                <div style={{ float: 'left' }}>
                  <StylizedBulletList items={includesFeaturesList} />
                </div>
                <img
                  src={smsStat}
                  alt={t('sms-plans.dialog.open-rates-as-high-as-98-pct')}
                  style={{ height: 160, width: 200 }}
                />
              </div>
            </div>
            <div
              style={{
                flexGrow: 1.2,
                borderRight: '1px solid #33333316',
              }}
              className="sms-plan-cards"
            >
              <div style={{ zIndex: 1, position: 'relative' }}>
                {SMSTiers && SMSTiers.length > 0 && prepayDiscountPercentage != null && (
                  <>
                    <SMSTiersCard
                      smsTier={SMSTiers[0]}
                      prepayDiscountPercentage={prepayDiscountPercentage}
                      selectedSMSTier={smsSelection}
                      selectTierCallback={(min, max, price) => {
                        trackAction({ action: 'stp-sms-tier-select' });
                        setSmsSelection({ min, max, price });
                      }}
                      currency={currency}
                      description={t('sms-plans.dialog.tier-1-description')}
                      showCardDecimals={showCardDecimals}
                      disabled={addSmsAddon || isCurrent(SMSTiers[0])}
                      isPremium={isPremium}
                      isCurrent={isCurrent(SMSTiers[0])}
                    />
                    <SMSTiersCard
                      smsTier={SMSTiers[1]}
                      prepayDiscountPercentage={prepayDiscountPercentage}
                      selectedSMSTier={smsSelection}
                      selectTierCallback={(min, max, price) => {
                        trackAction({ action: 'stp-sms-tier-select' });
                        setSmsSelection({ min, max, price });
                      }}
                      currency={currency}
                      description={t('sms-plans.dialog.tier-2-description')}
                      showCardDecimals={showCardDecimals}
                      disabled={addSmsAddon || isCurrent(SMSTiers[1])}
                      isRecommended={!isCurrent(SMSTiers[1])}
                      isCurrent={isCurrent(SMSTiers[1])}
                    />
                    <SMSTiersCard
                      smsTier={SMSTiers[2]}
                      prepayDiscountPercentage={prepayDiscountPercentage}
                      selectedSMSTier={smsSelection}
                      selectTierCallback={(min, max, price) => {
                        trackAction({ action: 'stp-sms-tier-select' });
                        setSmsSelection({ min, max, price });
                      }}
                      currency={currency}
                      description={t('sms-plans.dialog.tier-3-description')}
                      showCardDecimals={showCardDecimals}
                      disabled={addSmsAddon || isCurrent(SMSTiers[2])}
                      isCurrent={isCurrent(SMSTiers[2])}
                    />
                  </>
                )}
              </div>
              <div style={{ marginTop: -50, paddingBottom: 50 }} ref={comparePlansRef} />
              {!SMSTiers || SMSTiers.length === 0 || prepayDiscountPercentage == null ? (
                <div style={{ padding: 160 }}>
                  <Spinner size="large" />
                </div>
              ) : (
                <div className="tiers-content">
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    {/* eslint-disable jsx-a11y/anchor-is-valid */}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowAllTiers(((showAllTiers + 1) % 100) + 1);
                      }}
                    >
                      {`${t('sms-plans.dialog.view-more-options')}  `}
                      {showAllTiers > 0 && (!SMSTiers || SMSTiers.length === 0) ? (
                        <Spinner />
                      ) : (
                        <Icon style={{ textDecoration: 'none' }} glyph="chevronSouth" />
                      )}
                    </a>
                  </div>
                  {showAllTiers > 0 && (
                    <SMSTiersTable
                      loading={loadingSMSTiers}
                      allDisabled={addSmsAddon}
                      currency={currency}
                      selectedSMSTier={smsSelection}
                      prepayDiscountPercentage={prepayDiscountPercentage}
                      setSelectedSMSTier={(min, max, price) => {
                        trackAction({ action: 'stp-sms-tier-select' });
                        setSmsSelection({ min, max, price });
                      }}
                      // eslint-disable-next-line no-nested-ternary
                      SMSTiers={showAllTiers ? (SMSTiers && SMSTiers.length ? SMSTiers.slice(3) : []) : []}
                      contactCountLowEnd={contactCount}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="sticky-wrapper sms-order-summary" style={{ minWidth: 340 }}>
            <div className="order-summary-container">
              <OrderSummary
                data-qe-id="sms-add-on-order-summary"
                loading={loading}
                currency={currency}
                rating={rating}
                discountAmount={discountAmount}
                discountError={discountError}
                discountCode={discountCode}
                appliedDiscountCode={appliedDiscountCode}
                setDiscountCode={setDiscountCode}
                contactTierLabel={contactTierLabel}
                promoEnabled={promoEnabled}
                applyDiscountCode={(code) => setAppliedDiscountCode(code ? code.toUpperCase() : code)}
                nextBillDate={nextBillDate}
                nextBillDateObject={nextBillDateObject}
                smsSelectionMin={smsSelection.min}
                smsSelectionMax={smsSelection.max}
              />
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

SMSTierDialogWithOrderSummaryV2.defaultProps = {
  smsPurchasedCallback: null,
  closeOnSuccess: false,
};

SMSTierDialogWithOrderSummaryV2.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  closeOnSuccess: PropTypes.bool,
  smsPurchasedCallback: PropTypes.func,
};

export default withTranslation()(SMSTierDialogWithOrderSummaryV2);
